import React from "react";
import { Link } from "react-router-dom";
import { Star, Shield, Users, Clock, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Star className="h-6 w-6 text-amber-500 fill-current" />
                <span className="text-blue-900 font-semibold">Veteran-Owned & Family-Operated Since 2020</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Quality Transport Services
              </h1>
              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                Professional Non-Emergency Medical Transportation serving Calhoun County 
                and surrounding areas. Founded by military veterans who understand the importance 
                of reliability, safety, and treating every client with dignity and respect.
              </p>
              <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-6">
                <p className="text-amber-800 font-medium">
                  <strong>Our Mission:</strong> We primarily transport for insurance-covered trips but also accept self-pay clients. 
                  Every veteran, family member, and community member deserves safe, reliable transportation to their medical care.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="bg-blue-900 hover:bg-blue-800">
                  <Link to="/book-ride">Book a Ride</Link>
                </Button>
                <Button variant="outline" size="lg" className="border-blue-900 text-blue-900 hover:bg-blue-50">
                  <Phone className="h-4 w-4 mr-2" />
                  (269) 248-3514
                </Button>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/24415252/pexels-photo-24415252.jpeg"
                alt="Wheelchair accessible van with ramp"
                className="rounded-lg shadow-xl w-full h-96 object-cover"
              />
              <div className="absolute -bottom-4 -left-4 bg-white p-4 rounded-lg shadow-lg">
                <div className="flex items-center space-x-2">
                  <Shield className="h-6 w-6 text-green-600" />
                  <span className="font-semibold text-gray-900">Safe & Reliable</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Quality Transport Services?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We're committed to providing safe, reliable, and respectful transportation services
              with the care and attention you deserve.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="text-center p-6 hover:shadow-lg transition-shadow duration-300">
              <CardContent className="space-y-4">
                <Shield className="h-12 w-12 text-blue-900 mx-auto" />
                <h3 className="text-xl font-semibold text-gray-900">Veteran-Owned</h3>
                <p className="text-gray-600">
                  Founded and operated by veterans who understand the importance of 
                  reliability, respect, and service excellence.
                </p>
              </CardContent>
            </Card>
            <Card className="text-center p-6 hover:shadow-lg transition-shadow duration-300">
              <CardContent className="space-y-4">
                <Users className="h-12 w-12 text-blue-900 mx-auto" />
                <h3 className="text-xl font-semibold text-gray-900">Family-Operated</h3>
                <p className="text-gray-600">
                  A family business that treats every client like family, providing 
                  personalized care and attention to your transportation needs.
                </p>
              </CardContent>
            </Card>
            <Card className="text-center p-6 hover:shadow-lg transition-shadow duration-300">
              <CardContent className="space-y-4">
                <Clock className="h-12 w-12 text-blue-900 mx-auto" />
                <h3 className="text-xl font-semibold text-gray-900">Flexible Scheduling</h3>
                <p className="text-gray-600">
                  We request 24 hours' notice for long-distance trips but are happy 
                  to work with last-minute requests whenever possible.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Comprehensive Transportation Services
              </h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-amber-500 rounded-full mt-2"></div>
                  <p className="text-gray-700">Wheelchair accessible vehicles available</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-amber-500 rounded-full mt-2"></div>
                  <p className="text-gray-700">Ambulatory transportation services</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-amber-500 rounded-full mt-2"></div>
                  <p className="text-gray-700">Local and long-distance trips</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-amber-500 rounded-full mt-2"></div>
                  <p className="text-gray-700">Insurance-covered and self-pay options</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-amber-500 rounded-full mt-2"></div>
                  <p className="text-gray-700">Door-to-door support and appointment reminders</p>
                </div>
              </div>
              <Button asChild className="mt-6 bg-blue-900 hover:bg-blue-800">
                <Link to="/services">Learn More About Our Services</Link>
              </Button>
            </div>
            <div>
              <img
                src="https://images.pexels.com/photos/18442877/pexels-photo-18442877.jpeg"
                alt="Medical transport minivan"
                className="rounded-lg shadow-xl w-full h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Schedule Your Transportation?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Get safe, reliable, and respectful transportation services from a 
            veteran-owned and family-operated company you can trust.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="secondary" className="bg-amber-500 hover:bg-amber-600 text-white">
              <Link to="/book-ride">Book a Ride Now</Link>
            </Button>
            <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-blue-900">
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;